import ProductCard from '@/components/ProductCard/ProductCard'
import type {Product} from '@/models/productModel'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import style from '@/views/Stores/StoreFront.module.scss'

const ITEMS_PER_ROW = 4

interface StoreSectionProps {
  title: string
  products: Product[]
  isExpanded: boolean
  onToggle: (title: string) => void
}

export default function StoreSection({title, products, isExpanded, onToggle}: StoreSectionProps) {
  const baseId = useId()

  if (products.length === 0) return null

  const productsToShow = isExpanded ? products : products.slice(0, ITEMS_PER_ROW)
  const isNonAmazonProduct = (product: Product): boolean => {
    return product.retailer !== 'amazon' && (product as {seller?: string}).seller !== 'amazon'
  }

  return (
    <VisuallyHidden as='section' aria-label={title} isVisible={products.some(isNonAmazonProduct)}>
      <div className={style.SectionHeader}>
        <h2 className={style.SectionTitle}>{title}</h2>
        <p className={style.SectionSubtitle}>
          Discover the latest additions to our collection. Don't miss out - grab them before they're
          gone!
        </p>
      </div>
      <div
        className={style.Products}
        id={baseId}
        style={{'--items-per-row': ITEMS_PER_ROW} as React.CSSProperties}
      >
        {productsToShow.map((product, idx) => (
          <VisuallyHidden
            as='div'
            key={`${product.retailer}-${product.id}`}
            className={style.Product}
            id={`${baseId}-item-${idx + 1}`}
            tabIndex={-1}
            isVisible={isNonAmazonProduct(product)}
            ref={el => {
              if (isExpanded && idx === ITEMS_PER_ROW && el) {
                el.focus()
              }
            }}
          >
            <ProductCard product={product} headingLevel={3} />
          </VisuallyHidden>
        ))}
      </div>
      {products.length > ITEMS_PER_ROW && (
        <div className={style.ViewMoreWrapper}>
          <button
            type='button'
            className={style.ViewMoreBtn}
            aria-expanded={isExpanded}
            aria-controls={baseId}
            onClick={() => {
              onToggle(title)
            }}
          >
            {isExpanded ? 'View less' : 'View more'}{' '}
            <span aria-hidden className={style.Chevron}>
              ▾
            </span>
          </button>
        </div>
      )}
    </VisuallyHidden>
  )
}
